/**
 * Commercial Readiness: Demo Workspace Manager
 * 
 * Isolated demo environment for sales presentations
 * Features: One-click reset, demo data seeding, demo mode indicator
 */

import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  RefreshCw,
  Database,
  AlertTriangle,
  CheckCircle2,
  Presentation,
  Shield,
  Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoWorkspaceManager = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [stats, setStats] = useState(null);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const fetchDemoStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/demo/stats`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching demo stats:', error);
      toast.error('Failed to load demo statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDemo = async () => {
    setSeeding(true);
    try {
      const response = await fetch(`${API}/demo/seed`, {
        method: 'POST',
        headers: authHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Demo data seeded successfully! Created ${data.total_records} records`);
        await fetchDemoStats();
      } else {
        toast.error('Failed to seed demo data');
      }
    } catch (error) {
      toast.error('Error seeding demo data');
    } finally {
      setSeeding(false);
    }
  };

  const handleResetDemo = async () => {
    if (!window.confirm('Are you sure you want to reset the demo workspace? This will delete all demo data and reseed with fresh sample data.')) {
      return;
    }
    
    setResetting(true);
    try {
      const response = await fetch(`${API}/demo/reset`, {
        method: 'POST',
        headers: authHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Demo workspace reset successfully! Recreated ${data.total_records} records`);
        await fetchDemoStats();
      } else {
        toast.error('Failed to reset demo workspace');
      }
    } catch (error) {
      toast.error('Error resetting demo workspace');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteDemo = async () => {
    if (!window.confirm('Are you sure you want to DELETE ALL demo data? This cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/demo/delete`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      
      if (response.ok) {
        toast.success('Demo workspace deleted successfully');
        await fetchDemoStats();
      } else {
        toast.error('Failed to delete demo workspace');
      }
    } catch (error) {
      toast.error('Error deleting demo workspace');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchDemoStats();
  }, []);

  return (
    <div className="page-stack" data-testid="demo-workspace-manager">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Master Admin Only — Sales Demonstration Environment</p>
          <h2>Demo Workspace Manager</h2>
          <div className="hero-stats">
            <span>{stats?.demo_leads || 0} Demo Leads</span>
            <span>{stats?.demo_clients || 0} Demo Clients</span>
            <span>{stats?.demo_audits || 0} Demo Audits</span>
          </div>
        </div>
        <Button 
          className="primary-btn" 
          onClick={handleResetDemo}
          disabled={resetting}
          data-testid="reset-demo-button"
        >
          {resetting ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              Resetting...
            </>
          ) : (
            <>
              <RefreshCw size={16} />
              One-Click Reset
            </>
          )}
        </Button>
      </section>

      {/* Security Notice */}
      <Alert className="bg-blue-500/20 border-blue-500/30">
        <Shield className="h-4 w-4 text-blue-300" />
        <AlertDescription>
          <strong className="text-blue-300">Isolated Environment:</strong>
          <span className="text-blue-300/90 ml-2">
            Demo data is completely isolated from production using the <code className="px-1.5 py-0.5 bg-blue-500/30 rounded text-xs">demo_</code> collection prefix. 
            No production data will be affected by demo operations.
          </span>
        </AlertDescription>
      </Alert>

      {/* Action Cards */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-panel border-white/10" data-testid="seed-demo-card">
          <CardHeader>
            <CardTitle className="text-white/90 text-sm flex items-center gap-2">
              <Database className="h-5 w-5 text-[var(--gold)]" />
              Seed Demo Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-white/70">
              Add sample leads, clients, audits, and proposals to the demo workspace for presentations.
            </p>
            <Button
              className="w-full ghost-btn"
              onClick={handleSeedDemo}
              disabled={seeding}
              data-testid="seed-demo-btn"
            >
              {seeding ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Seeding...
                </>
              ) : (
                <>
                  <Database size={16} />
                  Seed Demo Data
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10" data-testid="reset-demo-card">
          <CardHeader>
            <CardTitle className="text-white/90 text-sm flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-[var(--gold)]" />
              Reset Demo Workspace
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-white/70">
              Delete all demo data and reseed with fresh samples. Perfect for resetting between presentations.
            </p>
            <Button
              className="w-full ghost-btn"
              onClick={handleResetDemo}
              disabled={resetting}
              data-testid="reset-demo-workspace-btn"
            >
              {resetting ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  One-Click Reset
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10" data-testid="delete-demo-card">
          <CardHeader>
            <CardTitle className="text-white/90 text-sm flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Delete Demo Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-white/70">
              Permanently delete all demo data without reseeding. Use after demonstrations are complete.
            </p>
            <Button
              className="w-full bg-red-500/20 hover:bg-red-500/30 border-red-500/30 text-red-300"
              onClick={handleDeleteDemo}
              disabled={loading}
              data-testid="delete-demo-btn"
            >
              <Trash2 size={16} />
              Delete All Demo Data
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Demo Statistics */}
      {stats && (
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Presentation className="h-8 w-8 text-[var(--gold)]" />
                <div>
                  <p className="text-xs text-white/60 uppercase tracking-wide">Demo Leads</p>
                  <strong className="text-2xl font-semibold text-white">{stats.demo_leads || 0}</strong>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-[var(--gold)]" />
                <div>
                  <p className="text-xs text-white/60 uppercase tracking-wide">Demo Clients</p>
                  <strong className="text-2xl font-semibold text-white">{stats.demo_clients || 0}</strong>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Database className="h-8 w-8 text-[var(--gold)]" />
                <div>
                  <p className="text-xs text-white/60 uppercase tracking-wide">Demo Audits</p>
                  <strong className="text-2xl font-semibold text-white">{stats.demo_audits || 0}</strong>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Shield className="h-8 w-8 text-green-400" />
                <div>
                  <p className="text-xs text-white/60 uppercase tracking-wide">Production Safe</p>
                  <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                    Isolated
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Instructions */}
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <CardTitle className="text-white/90 flex items-center gap-2">
            <Presentation className="h-5 w-5 text-[var(--gold)]" />
            Demo Mode Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-lg">
              <h4 className="text-sm font-semibold text-[var(--gold)] mb-2">How to Use Demo Mode</h4>
              <ol className="space-y-2 text-xs text-white/80 list-decimal list-inside">
                <li>Click "Seed Demo Data" or "One-Click Reset" to populate the demo workspace</li>
                <li>Demo data will be created with the <code className="px-1.5 py-0.5 bg-white/10 rounded">demo_</code> prefix</li>
                <li>Use the demo workspace for sales presentations and product demonstrations</li>
                <li>Demo data is completely isolated from production - safe to modify or delete</li>
                <li>Use "One-Click Reset" between presentations to restore fresh demo data</li>
                <li>Use "Delete All Demo Data" to clean up after demonstrations are complete</li>
              </ol>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border border-white/10 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-400 mb-2" />
                <h4 className="text-sm font-semibold text-white/90 mb-1">Safe Operations</h4>
                <ul className="text-xs text-white/70 space-y-1">
                  <li>• Seed demo data</li>
                  <li>• Reset demo workspace</li>
                  <li>• Delete demo data</li>
                  <li>• Modify demo records</li>
                </ul>
              </div>
              <div className="p-4 border border-white/10 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-400 mb-2" />
                <h4 className="text-sm font-semibold text-white/90 mb-1">Production Protected</h4>
                <ul className="text-xs text-white/70 space-y-1">
                  <li>• Production data isolated</li>
                  <li>• No cross-contamination</li>
                  <li>• Separate collections</li>
                  <li>• Master Admin only access</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoWorkspaceManager;
